<template>
    <div class="RiskRequirementCriteria">
        <div class="RiskRequirementCriteria__header">
            <div>Verify</div>
            <div>Criteria</div>
        </div>
        <!-- Warning/Error Messages -->
        <div v-if="hasErrorsToShow || hasDeprecatedCriteria">
            <RiskRequirementAlerts
                :show-validation-errors="showValidationErrors"
                :is-evaluation-rule-requirement-type-all-criteria-not-verify="
                    isEvaluationRuleRequirementTypeAllCriteriaNotVerify
                "
                :has-deprecated-criteria="hasDeprecatedCriteria"
                :is-invalid-criteria-message="isInvalidCriteriaMessage"
            />
        </div>

        <!-- Criteria Table View -->
        <div class="RiskRequirementCriteria__criteriaList">
            <RiskProfileCriteriaTable
                v-if="sortedValidCriteriaList && sortedValidCriteriaList.length > 0"
                :criteria-list="sortedValidCriteriaList"
                :templates-by-field-name="templatesByFieldName"
                :custom-properties="customProperties"
                :is-reference-starts-with-is-invalid="isReferenceStartsWithIsInvalid"
                :show-validation-errors="showValidationErrors"
                :can-manage-criteria="canManageCriteria"
                :can-open-settings="canOpenCriterionSettings"
                @open-settings="$emit('openSettings', $event)"
                @input="$emit('criteriaInput', $event)"
                @reference-accessed="$emit('reference-accessed', $event)"
                @validity-change="$emit('validityChange', $event)"
            />
            <div v-else class="RiskRequirementCriteria__emptyState">
                No criteria available for this requirement type.
            </div>

            <!-- Footer Actions -->
            <div class="RiskRequirementCriteria__footerAction">
                <CriterionAddButtonWithPopover
                    v-if="canManageCriteria"
                    :criterion-templates="availableCriterionTemplates"
                    :disabled="availableCriterionTemplates.length === 0"
                    @add="$emit('addCriterion', $event)"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    @import "../variables";

    .RiskRequirementCriteria {
        &__header {
            display: flex;
            align-items: center;
            justify-content: start;
            gap: 12px;
            padding: $padding-y $padding-x;
            border-bottom: 1px solid $border-color;
        }

        &__alerts {
            margin-bottom: 16px;
        }

        &__criteriaList {
            padding: $padding-y $padding-x;
        }

        &__emptyState {
            padding: 20px;
            text-align: center;
            color: var(--eidSecureBlue-50);
            font-style: italic;
        }

        &__footerAction {
            display: flex;
            margin-top: 20px;
            padding-top: 16px;
            border-top: 1px solid $border-color;
            justify-content: flex-end;
        }
    }
</style>

<script lang="ts">
    import { PropType } from 'vue';

    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { sortBy } from 'lodash';
    import differenceBy from 'lodash/differenceBy';

    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

    import { IsReferenceStartsWithIsInvalid } from '@/modules/decisioning-criteria/components/CreateEditRiskProfile/types';
    import CriterionAddButtonWithPopover
        from '@/modules/decisioning-criteria/components/CriterionAddButtonWithPopover/CriterionAddButtonWithPopover.vue';
    import RiskProfileCriteriaTable from '@/modules/decisioning-criteria/components/RiskProfileCriteriaTable/RiskProfileCriteriaTable.vue';
    import {
        EditableRequirementCriterion,
    } from '@/modules/decisioning-criteria/models/EditableRequirements.model';

    import RiskRequirementAlerts from './RiskRequirementAlerts.vue';

    @Component({
        emits: [ 'addCriterion', 'criteriaInput', 'openSettings', 'reference-accessed', 'validityChange' ],
        components: {
            RiskProfileCriteriaTable,
            CriterionAddButtonWithPopover,
            RiskRequirementAlerts,
        },
    })
    export default class RiskRequirementCriteria extends Vue {
        @Prop()
        private requirementType!: TprmRequirementType;

        @Prop({ type: Boolean, default: false })
        private hasDeprecatedCriteria!: boolean;

        @Prop({ type: Array, default: () => [] })
        private criteriaList!: EditableRequirementCriterion[];

        @Prop({ type: Object, default: () => ({}) })
        private templatesByFieldName!: Record<string, CriterionTemplate>;

        @Prop({ type: Array, default: () => [] })
        private customProperties!: CustomProperty[];

        @Prop({ type: Function as PropType<IsReferenceStartsWithIsInvalid>, default: () => () => false })
        private isReferenceStartsWithIsInvalid!: IsReferenceStartsWithIsInvalid;

        @Prop({ type: Array, default: () => [] })
        private criteriaTemplates!: CriterionTemplate[];

        @Prop({ type: Array, default: () => [] })
        private evaluationRuleRequirementTypes!: TprmRequirementType[];

        @Prop({ type: Boolean, default: false })
        private showValidationErrors!: boolean;

        @Prop({ type: Boolean, default: true })
        private canManageCriteria!: boolean;

        @Prop({ type: Boolean, default: true })
        private canOpenCriterionSettings!: boolean;

        private get sortedValidCriteriaList(): EditableRequirementCriterion[] {
            const filtered = this.criteriaList.filter((criterion) =>
                this.templatesByFieldName[criterion.field] !== undefined,
            );
            return sortBy(
                filtered,
                (criterion) => this.templatesByFieldName[criterion.field]?.displayMetadata.title?.toLowerCase(),
            );
        }

        private get availableCriterionTemplates(): CriterionTemplate[] {
            // templatesByFieldName contains prefiltered templates that including collateral enabledness check
            // need to rename templatesByFieldName to be more clear about it, while the criteriaTemplate is full set
            return differenceBy(Object.values(this.templatesByFieldName), this.criteriaList, 'field')
                .filter((template) => !template.deprecated);
        }

        private get isEvaluationRuleRequirementTypeAllCriteriaNotVerify(): boolean {
            const isRequirementTypeInEvaluationRule = this.evaluationRuleRequirementTypes
                .includes(this.requirementType);
            const allCriteriaAreNotVerify = this.criteriaList.every((criterion) => !criterion.verify);
            return isRequirementTypeInEvaluationRule && allCriteriaAreNotVerify;
        }

        private get isInvalidCriteriaMessage(): boolean {
            return this.isReferenceStartsWithIsInvalid(this.requirementType);
        }

        private get hasErrorsToShow(): boolean {
            return this.showValidationErrors && (
                this.isEvaluationRuleRequirementTypeAllCriteriaNotVerify ||
                this.isInvalidCriteriaMessage
            );
        }
    }
</script>
